import { environmentHttpClient } from "client/Api/httpClient";
import { SecretValue } from "client/Api/Apps/common";

export enum ServiceHostingLocation {
	Office365 = 1,
	OnPremises = 2
}

export enum SingleSignOnType {
	WsFederation = 1,
	OAuth2 = 2
}

export enum CustomSignOutRedirectUrlActionType {
	None = 0,
	Disabled = 1,
	Enabled = 2
}

export type WsFederationViewModel = {
	metadataDocumentUrl: string;
};

export type OAuth2ViewModel = {
	authority: string;
	clientId: string;
	keyContainsValue: boolean;
	domainHint: string;
	shouldSignOutWhenRefreshTokenExpires: boolean;
	isMobileAppMicrosoftAuthenticatorSignInEnabled: boolean;
};

export type SingleSignOnPageModel = {
	singleSignOnType: SingleSignOnType | null;
	signOutRedirectUrl: string;
	wsFederationViewModel: WsFederationViewModel;
	oAuth2ViewModel: OAuth2ViewModel;
	isUpdateRequired: boolean;
};

export type SingleSignOnSettingsViewModel = {
	model: SingleSignOnPageModel;
	availableSingleSignOnTypes: SingleSignOnType[];
	hasCredentialsForCurrentUser: boolean;
	isAutomaticSetupAvailable: boolean;
	activeDirectoryHostingLocation: ServiceHostingLocation;
	productName: string;
	configurationHelpUrl: string;
	signOutHelpUrl: string;
};

export type SignOutRedirectUrlModel = {
	url: string;
	isConfigurable: boolean;
};

export type WsFederationConfigurationSaveModel = {
	metadataDocumentUrl: string;
	signOutRedirectUrlConfiguration: SignOutRedirectUrlConfiguration;
};

export function getSingleSignOnSettingsViewModel(signal?: AbortSignal): Promise<SingleSignOnSettingsViewModel> {
	return environmentHttpClient.get<SingleSignOnSettingsViewModel>(
		"/UiApi/SingleSignOnSettings",
		undefined,
		{ signal }
	);
}

export function configureWsFederationSingleSignOn(
	model: WsFederationConfigurationSaveModel,
	signal?: AbortSignal
): Promise<void> {

	return environmentHttpClient.put<void>(
		"/UiApi/SingleSignOnSettings/ConfigureWsFederationSingleSignOn",
		model,
		{ signal }
	);
}

export type SignOutRedirectUrlConfiguration = {
	url: string;
	action: CustomSignOutRedirectUrlActionType;
};

export type OAuth2ConfigurationSaveModel = {
	authority: string;
	clientId: string;
	key: SecretValue;
	domainHint: string;
	signOutRedirectUrlConfiguration: SignOutRedirectUrlConfiguration;
	shouldSignOutWhenRefreshTokenExpires: boolean;
};

export function configureOAuth2SingleSignOn(
	model: OAuth2ConfigurationSaveModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		"/UiApi/SingleSignOnSettings/ConfigureOAuth2SingleSignOn",
		model,
		{ signal }
	);
}

export function getUrlForOAuth2ConfigurationVerification(
	model: OAuth2ConfigurationSaveModel,
	signal?: AbortSignal
): Promise<{ url: string; }> {
	return environmentHttpClient.post<{ url: string; }>(
		"/UiApi/SingleSignOnSettings/GetUrlForOAuth2ConfigurationVerification",
		model,
		{ signal }
	);
}

export type CreateOAuth2ApplicationInAzureModel = {
	permissionToCreateAzureApplication: boolean;
	userProfilePermissions: boolean;
	sharePointPermissions: boolean;
	exchangePermissions: boolean;
	powerBiPermissions: boolean;
	windowsVirtualDesktopPermissions: boolean;
	windowsVirtualDesktopArmPermissions: boolean;
	shouldEnableMicrosoftAuthenticatorMobileSignIn: boolean;
	password: string | null;
};

export function createOAuth2AzureApplication(
	model: CreateOAuth2ApplicationInAzureModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.post<void>(
		"/UiApi/SingleSignOnSettings/CreateOAuth2AzureApplication",
		model,
		{ signal }
	);
}

export type OAuth2EnableMicrosoftAuthenticatorMobileSignInResult = {
	wasSuccessful: false;
	reason: string;
} | {
	wasSuccessful: true;
};

export type SingleSignOnPasswordModel = {
	password: string;
};

export const enableMicrosoftAuthenticatorMobileSignIn = (
	model: SingleSignOnPasswordModel,
	signal?: AbortSignal,
): Promise<OAuth2EnableMicrosoftAuthenticatorMobileSignInResult> => {
	return environmentHttpClient.put<OAuth2EnableMicrosoftAuthenticatorMobileSignInResult>(
		"/UiApi/SingleSignOnSettings/EnableMicrosoftAuthenticatorMobileSignIn",
		model,
		{ signal }
	);
};

export type OAuth2ApplicationUpdateResult = { wasSuccessful: false; reason: string; }
	| { wasSuccessful: true; adminConsentUrl: string; };

export type VerifyPermissionsResult = { permissionsAreUpToDate: false; }
	| { permissionsAreUpToDate: true; adminConsentUrl: string; };

export function updateSingleSignOn(
	password: string,
	signal?: AbortSignal
): Promise<OAuth2ApplicationUpdateResult> {
	return environmentHttpClient.put<OAuth2ApplicationUpdateResult>(
		"/UiApi/SingleSignOnSettings/UpdateSingleSignOnApplication",
		{ password },
		{ signal }
	);
};

export function verifyPermissions(signal?: AbortSignal): Promise<VerifyPermissionsResult> {
	return environmentHttpClient.put<VerifyPermissionsResult>(
		"/UiApi/SingleSignOnSettings/VerifyPermissions",
		undefined,
		{ signal }
	);
};

export type OAuth2SecretKeyExpirationModel =
| {
	shouldShowExpirationMessage: true;
	expirationDate: Date | string;
}
| {
	shouldShowExpirationMessage: false;
};